import * as React from "react";
import ShiftForm from "./ShiftForm";
import { useState, useEffect } from "react";
import daysOfWeek from "./helpers/daysOfWeek";
import { Shift, WorkSchedule } from "./types";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SecondaryGrayButton
} from "@velaro/velaro-component-lib";
import {
  SecondaryBlueButton,
  PrimaryBlueButton
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

interface AgentOption {
  id: number;
  firstName: string;
  lastName: string;
}

interface Props {
  schedule: WorkSchedule;
  onConfirm: (schedule: WorkSchedule) => void;
  onReturn: () => void;
  isNewSchedule: boolean;
}

export default function ScheduleEditor(props: Props) {
  const [name, setName] = useState<string>(props.schedule.name);
  const [shifts, setShifts] = useState<Shift[]>([...props.schedule.shifts]);
  const [userIds, setUserIds] = useState<number[]>([...(props.schedule.userIds ?? [])]);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const api = useApi();

  useEffect(() => {
    api.messaging.get("Users/List").then(async (resp) => {
      if (resp.ok) {
        const data = await resp.json();
        setAgents(
          Array.isArray(data)
            ? data.map((u: any) => ({ id: u.id, firstName: u.firstName ?? "", lastName: u.lastName ?? "" }))
            : []
        );
      }
    }).catch(() => {});
  }, [api.messaging]);

  function onToggleAgent(agentId: number) {
    setUserIds((prev) =>
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    );
  }

  function onChangeName(event: any) {
    setName(event.currentTarget.value);
  }

  function onAddShift() {
    if (shifts.length) {
      const newShift: any = { ...shifts[shifts.length - 1] };
      if (newShift.startDayOfWeek !== daysOfWeek.Saturday) newShift.startDayOfWeek++;
      if (newShift.endDayOfWeek !== daysOfWeek.Saturday) newShift.endDayOfWeek++;
      setShifts([...shifts, newShift]);
      return;
    }
    const newShift = {
      startDayOfWeek: daysOfWeek.Monday,
      startHour: 9,
      startMinute: 0,
      endDayOfWeek: daysOfWeek.Monday,
      endHour: 17,
      endMinute: 0
    };
    setShifts([...shifts, newShift]);
  }

  function onChangeShift(shift: Shift, i: number) {
    shifts[i] = shift;
    setShifts([...shifts]);
  }

  function onDeleteShift(i: number) {
    shifts.splice(i, 1);
    setShifts([...shifts]);
  }

  function onDiscard() {
    setName(props.schedule.name);
    setShifts([...props.schedule.shifts]);
    setUserIds([...(props.schedule.userIds ?? [])]);
    props.onReturn();
  }

  function onConfirm() {
    if (!name) return;
    props.onConfirm({ ...props.schedule, name, shifts, userIds });
  }

  function renderShifts() {
    if (!shifts.length) {
      return <span className="shift-form form-team">Please add a shift</span>;
    }
    return (
      <React.Fragment>
        {shifts.map((shift, i) => (
          <ShiftForm
            key={i}
            shift={shift}
            onChange={(shift) => onChangeShift(shift, i)}
            onDelete={() => onDeleteShift(i)}
          />
        ))}
      </React.Fragment>
    );
  }

  function renderAgentPicker() {
    if (agents.length === 0) {
      return <div className="text-xs text-gray-400 p-3">No agents available</div>;
    }
    return (
      <div className="p-3 max-h-64 overflow-y-auto">
        {agents.map((agent) => {
          const checked = userIds.includes(agent.id);
          return (
            <label
              key={agent.id}
              className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 px-1 rounded"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggleAgent(agent.id)}
                className="rounded"
              />
              <span className="text-sm">
                {agent.firstName} {agent.lastName}
              </span>
            </label>
          );
        })}
      </div>
    );
  }

  return (
    <form className="schedule-form">
      <ModalHeader
        title={props.isNewSchedule ? "Add Schedule" : "Edit Schedule"}
        onClose={props.onReturn}
      />
      <div className="flex">
        <div className="editor-section flex-none border-r" style={{ minWidth: 180 }}>
          <div>
            <div className="panel-heading bg-gray-100 border-b p-3">
              <h3 className="panel-title text-sm font-medium">Name schedule</h3>
            </div>
            <div className="p-3">
              <input
                required
                type="text"
                className="input"
                value={name}
                onChange={onChangeName}
              />
            </div>
          </div>
        </div>
        <div className="editor-section flex-1 shift-section">
          <div className="panel-heading flex bg-gray-100 border-b p-3">
            <h3 className="panel-title text-sm font-medium">Shift hours</h3>
            <button type="button" className="ml-auto underline text-sm" onClick={onAddShift}>
              Add shift
            </button>
          </div>
          <div className="panel-body">
            <div className="p-3">{renderShifts()}</div>
          </div>
        </div>
        <div className="editor-section flex-none border-l" style={{ minWidth: 200 }}>
          <div>
            <div className="panel-heading bg-gray-100 border-b p-3">
              <h3 className="panel-title text-sm font-medium">
                Assigned agents
                {userIds.length > 0 && (
                  <span className="ml-1 text-cornflower-blue-600 font-normal">({userIds.length})</span>
                )}
              </h3>
            </div>
            <div className="panel-body">{renderAgentPicker()}</div>
          </div>
        </div>
      </div>
      <ModalFooter>
        <div className="p-3 flex justify-end gap-2">
          {!props.isNewSchedule && (
            <SecondaryBlueButton onClick={onDiscard} label="Cancel" />
          )}
          <PrimaryBlueButton onClick={onConfirm} label="Save Changes" />
        </div>
      </ModalFooter>
    </form>
  );
}
